import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Eye, Search } from 'lucide-react';
import Modal from '../../common/Modal';
import Pagination from '../../common/Pagination';
import Button from '../../common/Button';
import Badge from '../../common/Badge';
import FormControl from '../../common/FormControl';
import Toast, { ToastMessage } from '../../common/Toast';
import { capaArchiveService } from '../../../services/capaArchiveService';
import SendToInvestigationModal from '../SendToInvestigationModal';
import InvestigationView from '../../../views/capa/InvestigationView';
import extractApiError from '../../../utils/apiError';
import NoDataFound from '../../common/NoDataFound';
import Skeleton from '../../common/Skeleton';
import { usePermission } from '../../../contexts/PermissionContext';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';

const ClosedInvestigationsPanel: React.FC = () => {
    const { allowed: canView, loading: viewLoading } = usePermission('CAP_INVESTIGATIONS', 'view');
    const { formatDate } = useEntitySettings();
    const [openInvId, setOpenInvId] = useState<number | null>(null);
    const [rows, setRows] = useState<any[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [reInvFor, setReInvFor] = useState<any | null>(null);

    const load = useCallback(async () => {
        if (!canView) return;
        try {
            setLoading(true);
            const { data, total } = await capaArchiveService.closedInvestigations({
                q: search || undefined,
                limit: pageSize,
                offset: (page - 1) * pageSize,
            });
            setRows(data);
            setTotalRecords(total);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load') });
        } finally { setLoading(false); }
    }, [search, page, pageSize, canView]);

    useEffect(() => { setPage(1); }, [search, pageSize]);
    useEffect(() => { load(); }, [load]);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to archived investigations." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search closed investigations…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_capa_closed_inv_search"
                    />
                </div>
            </div>
            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Issue</th>
                                        <th className="table-list-th">Owner</th>
                                        <th className="table-list-th">Opened</th>
                                        <th className="table-list-th">Closed</th>
                                        <th className="table-list-th table-list-cell--status">Status</th>
                                        <th className="table-list-th table-list-th--right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', 'sm', 'sm', 'pill', { width: 'icon', align: 'right' }]} />}
                                    {!loading && rows.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="table-list-cell">
                                                <NoDataFound
                                                    title={search ? 'No Matching Investigations' : 'No Closed Investigations'}
                                                    message={search ? 'No closed investigations match your search.' : 'No investigations have been closed yet.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && rows.map((r, idx) => (
                                        <tr key={r.id} className="table-list-row">
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title">{r.issue_code}</span>
                                                <span className="table-list-cell-sub">{r.issue_name}</span>
                                            </td>
                                            <td className="table-list-cell">{r.owner_name || `user#${r.owner_user_id}`}</td>
                                            <td className="table-list-cell">{r.opened_on ? formatDate(r.opened_on) : '—'}</td>
                                            <td className="table-list-cell">{r.closed_on ? formatDate(r.closed_on) : '—'}</td>
                                            <td className="table-list-cell table-list-cell--status"><Badge variant="neutral" size="sm">Closed</Badge></td>
                                            <td className="table-list-cell table-list-cell--actions">
                                                <div className="flex items-center gap-1 justify-end">
                                                    <Button variant="outline" icon={Eye} onClick={() => setOpenInvId(r.id)} data-testid={`BTN_capa_closed_inv_row_${r.id}_view`}>View</Button>
                                                    <Button variant="fill" icon={RefreshCw} onClick={() => setReInvFor(r)} data-testid={`BTN_capa_closed_inv_row_${r.id}_reinvestigate`}>Re-investigate</Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination page={page} pageSize={pageSize} totalRecords={totalRecords} onPageChange={setPage} onPageSizeChange={setPageSize} pageSizeOptions={[10, 25, 50, 100]} recordLabel="investigations" />
                    </div>
                </div>
            </div>

            {reInvFor && (
                <SendToInvestigationModal
                    isOpen={!!reInvFor}
                    onClose={() => setReInvFor(null)}
                    issueId={reInvFor.issue_id}
                    issueCode={reInvFor.issue_code}
                    issueName={reInvFor.issue_name}
                    presetOwnerId={reInvFor.owner_user_id}
                    onCreated={(newId) => {
                        setReInvFor(null);
                        setToast({ type: 'success', message: 'New investigation opened.' });
                        setOpenInvId(newId);
                    }}
                />
            )}

            {openInvId !== null && (
                <Modal isOpen={true} onClose={() => { setOpenInvId(null); load(); }} size="xl">
                    <InvestigationView
                        investigationIdProp={openInvId}
                        onClose={() => { setOpenInvId(null); load(); }}
                    />
                </Modal>
            )}

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default ClosedInvestigationsPanel;
